import { useEffect, useRef, useState } from "react";
import {
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  InputLabel,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import MDButton from "components/MDButton";
import { grey } from "@mui/material/colors";
import MDSnackbar from "components/MDSnackbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDDropDown from "components/MDDropDown";
import { useMaterialUIController } from "context";
import { paymentGatewayValidationSchema } from "services/validation";
import { assignSetting } from "store/slice/places/placeSlice";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getPlaceById } from "store/slice/places/placeSlice";
import { getConnectAccounts } from "store/slice/paymentGateway/paymentGatewaySlice";
import { setConnectAccounts } from "store/slice/paymentGateway/paymentGatewaySlice";
import { setSinglePlace } from "store/slice/places/placeSlice";
import { setSelectedPlace } from "store/slice/places/placeSlice";

function PaymentGateway() {
  const formikRef = useRef(null);
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const color = grey[900];
  const dispatch = useDispatch();
  const location = useLocation();
  const [connectAccountData, setConnectAccountData] = useState([]);

  const placesData = useSelector((state) => state.places?.selectedPlace);
  const singlePlace = useSelector((state) => state.places?.singlePlace);
  const connectAccounts = useSelector(
    (state) => state.paymentGateway?.connectAccounts,
  );
  let placeId =
    placesData?._id || new URLSearchParams(location?.search).get("placeId");

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  useEffect(() => {
    getConnectAccountList();
    getPlace();
  }, [placeId]);

  useEffect(() => {
    if (singlePlace?.paymentGateway) {
      formikRef?.current?.setFieldValue(
        "paymentGateway",
        singlePlace?.paymentGateway,
      );
      formikRef?.current?.setFieldValue(
        "connectAccountId",
        singlePlace?.connectAccountId,
      );
      formikRef?.current?.setFieldValue(
        "stripeAccount",
        get(singlePlace, "stripeConfiguration.name", "default"),
      );
    }
  }, [singlePlace]);

  function getPlace() {
    if (singlePlace?.paymentGateway) {
      formikRef?.current?.setFieldValue(
        "paymentGateway",
        singlePlace?.paymentGateway,
      );
      formikRef?.current?.setFieldValue(
        "connectAccountId",
        singlePlace?.connectAccountId,
      );
      formikRef?.current?.setFieldValue(
        "stripeAccount",
        get(singlePlace, "stripeConfiguration.name", "default"),
      );
    } else {
      dispatch(getPlaceById(placeId))
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(setSinglePlace(res?.place));
            formikRef?.current?.setFieldValue(
              "paymentGateway",
              res?.place?.paymentGateway,
            );
            formikRef?.current?.setFieldValue(
              "connectAccountId",
              res?.place?.connectAccountId,
            );
            formikRef?.current?.setFieldValue(
              "stripeAccount",
              get(res?.place, "stripeConfiguration.name", "default"),
            );
          }
        });
    }
  }

  function getConnectAccountList() {
    if (connectAccounts && connectAccounts.length < 1) {
      dispatch(getConnectAccounts())
        .unwrap()
        .then((res) => {
          if (res?.success) {
            dispatch(setConnectAccounts(res?.connectAccounts));
            setConnectAccountData(res?.connectAccounts);
          }
        })
        .catch((err) => {
          console.error("Error fetching connect accounts:", err);
          setNotification({
            ...notification,
            color: "error",
            title: "Error",
            content: err?.message || "Failed to fetch connect accounts",
            icon: "warning",
            show: true,
          });
        });
    } else {
      setConnectAccountData(connectAccounts);
    }
  }

  const handlePaymentGatewaySubmit = (value) => {
    const currentSetup = placesData?.setup || {};
    const currentSteps = currentSetup?.steps || {};

    const payload = {
      connectAccountId: value?.connectAccountId,
      paymentGateway: value?.paymentGateway,
      stripeConfiguration: {
        name: value?.stripeAccount,
      },
      setup: {
        ...currentSetup,
        steps: {
          ...currentSteps,
          generalSettings: true,
        },
      },
      placeId
    };
    dispatch(assignSetting(payload))
      .unwrap()
      .then((res) => {
        const success = res?.success;
        if (success) {
          dispatch(getPlaceById(placeId))
            .unwrap()
            .then((res) => {
              if (res?.success) {
                dispatch(setSelectedPlace(res?.place));
                dispatch(setSinglePlace(res?.place));
              }
            });
        }
        setNotification({
          ...notification,
          color: success ? "success" : "error",
          title: success ? "Success" : "Error",
          content: res?.message,
          icon: success ? "check" : "warning",
          show: true,
        });
      })
      .catch((err) => {
        console.error("Error assigning setting:", err);
        setNotification({
          ...notification,
          color: "error",
          title: "Error",
          content: err?.message || "Failed to assign payment gateway",
          icon: "warning",
          show: true,
        });
      });
  };

  return (
    <MDBox>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <Formik
        innerRef={formikRef}
        initialValues={{
          paymentGateway: "",
          connectAccountId: "",
          stripeAccount: "default",
        }}
        validationSchema={paymentGatewayValidationSchema}
        onSubmit={(value, action) => {
          handlePaymentGatewaySubmit(value);
        }}
      >
        {({ values, setFieldValue, handleSubmit, errors }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} className="mt-1">
              <Grid item xs={12} md={3} lg={3}>
                <MDBox
                  fontSize="12px"
                  mb={1.5}
                  bgColor={
                    values?.paymentGateway === "AUTHORIZENET"
                      ? sidenavColor
                      : color
                  }
                  sx={{
                    color: "primary.contrastText",
                    pt: 4,
                    pb: 4,
                    borderRadius: "16px",
                  }}
                  display="flex"
                  justifyContent="center"
                  onClick={() =>
                    setFieldValue("paymentGateway", "AUTHORIZENET")
                  }
                >
                  Authorize.net (PMC)
                </MDBox>
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <MDBox
                  fontSize="12px"
                  mb={1.5}
                  bgColor={
                    values?.paymentGateway === "AUTHORIZENET_PMC"
                      ? sidenavColor
                      : color
                  }
                  sx={{
                    color: "primary.contrastText",
                    pt: 4,
                    pb: 4,
                    borderRadius: "16px",
                  }}
                  display="flex"
                  justifyContent="center"
                  onClick={() =>
                    setFieldValue("paymentGateway", "AUTHORIZENET_PMC")
                  }
                >
                  Authorize.net (PMC-2)
                </MDBox>
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <MDBox
                  fontSize="12px"
                  mb={1.5}
                  bgColor={
                    values?.paymentGateway === "STRIPE" ? sidenavColor : color
                  }
                  sx={{
                    color: "primary.contrastText",
                    pt: 4,
                    pb: 4,
                    borderRadius: "16px",
                  }}
                  display="flex"
                  justifyContent="center"
                  onClick={() => setFieldValue("paymentGateway", "STRIPE")}
                >
                  Stripe (Dock)
                </MDBox>
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <MDBox
                  fontSize="12px"
                  mb={1.5}
                  bgColor={
                    values?.paymentGateway === "STRIPE_PMC"
                      ? sidenavColor
                      : color
                  }
                  sx={{
                    color: "primary.contrastText",
                    pt: 4,
                    pb: 4,
                    borderRadius: "16px",
                  }}
                  display="flex"
                  justifyContent="center"
                  onClick={() => setFieldValue("paymentGateway", "STRIPE_PMC")}
                >
                  Stripe (PMC)
                </MDBox>
              </Grid>

              {values?.paymentGateway === "STRIPE" && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="stripe-account-label">
                      Stripe Account
                    </InputLabel>
                    <MDDropDown
                      labelId="stripe-account-label"
                      id="stripe-account"
                      name="stripeAccount"
                      value={values.stripeAccount}
                      label="Stripe Account"
                      onChange={(_event, value) =>
                        setFieldValue("stripeAccount", value.props.value)
                      }
                    >
                      <MenuItem value="default">Default</MenuItem>
                      <MenuItem value="pmc">PMC</MenuItem>
                    </MDDropDown>
                  </FormControl>
                </Grid>
              )}

              {values?.paymentGateway === "STRIPE" && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <RadioGroup
                      name="select-connect-account-group"
                      value={values.connectAccountId}
                      onChange={(_event, value) =>
                        setFieldValue("connectAccountId", value)
                      }
                    >
                      <Grid container spacing={2} className="mt-1" width="100%">
                        {connectAccountData.map((item, index) => (
                          <Grid item xs={12} md={6} lg={4} key={index}>
                            <FormControlLabel
                              value={item.connectAccountId}
                              control={<Radio />}
                              label={
                                <Stack
                                  justifyContent="space-between"
                                  className="h-100"
                                >
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="regular"
                                  >
                                    {item.title}
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="regular"
                                  >
                                    {item.connectAccountId}
                                  </MDTypography>
                                </Stack>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} className="text-right">
                <MDTypography
                  display="block"
                  variant="span"
                  color="error"
                  sx={{ mb: 2 }}
                >
                  {errors?.paymentGateway}
                </MDTypography>
                <MDButton
                  color={sidenavColor}
                  disabled={
                    !values?.paymentGateway ||
                    (values?.paymentGateway === "STRIPE" &&
                      !values.connectAccountId)
                  }
                  variant="contained"
                  type="submit"
                >
                  Save
                </MDButton>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </MDBox>
  );
}

export default PaymentGateway;
